/**
 * The wall's masonry layout (Part 04 decision 4; Part 07 decision 4), pure.
 * One flat feed: cards go to the shortest column. A card placed on its
 * estimated height is placed again once it is first measured: from it on,
 * the cards refill the shortest columns, so wrong estimates leave no holes.
 * After that a card's column is kept: a re-measure or an arrival only moves
 * the cards below it in the same column. A full shortest-column pass runs on
 * a column-count or width-bucket change, or when the feed is not a head/tail
 * change of the previous one (a feed reset).
 */
import type { CSSProperties } from "react";

/** The centred column's widest (mockup `fit()`). */
const COLUMN_MAX_WIDTH = 1320;
/** Side padding: 5% of the scroller, within 16–72px. */
const MIN_PAD = 16;
const MAX_PAD = 72;
const PAD_SHARE = 0.05;
/** Columns drop while a card would be narrower than this. */
const MIN_COLUMN_WIDTH = 220;
/** Cards per row on a wide wall (Part 09 decision 1). */
const WALL_COLUMNS = 4;
/** Gutters per column count; 36px below 3, 20px on a narrow scroller. */
const GUTTERS: Record<number, number> = { 3: 36, 4: 32 };
const WIDE_GUTTER = 36;
const NARROW_GUTTER = 20;
const NARROW_FROM = 600;
/** Type grows with the column: 8.5px + 2.6% of it, within 13.5–18.5px. */
const FONT_MIN = 13.5;
const FONT_MAX = 18.5;
const FONT_BASE = 8.5;
const FONT_PER_PX = 0.026;
/** Pinned rows: 1.15 × the column width, within 260–400px. */
const PINNED_MIN = 260;
const PINNED_MAX = 400;
const PINNED_ASPECT = 1.15;
/** Measured heights are cached per 8px of column width. */
const WIDTH_BUCKET_PX = 8;

/**
 * The sticky header's height (`wall-header.tsx`: 20px above the 40px pills,
 * 40px below): the top of the scroller it covers.
 */
export const WALL_HEADER_HEIGHT = 100;

export interface WallGeometry {
  columns: number;
  columnWidth: number;
  /** Between columns, and between cards in a column. */
  gutter: number;
  /** x of the first column. */
  left: number;
  /** The centred column's width. */
  width: number;
  widthBucket: number;
  /** The cards' base type size, px. */
  fontSize: number;
  /** A Pinned row's height, px. */
  pinnedHeight: number;
}

export interface WallColumn {
  ids: readonly string[];
  tops: readonly number[];
  heights: readonly number[];
}

export interface WallCardBox {
  x: number;
  y: number;
  width: number;
  height: number;
  column: number;
}

export interface WallLayout {
  geometry: WallGeometry;
  columns: readonly WallColumn[];
  boxes: ReadonlyMap<string, WallCardBox>;
  height: number;
  /** Cards placed on an estimated height: their first measure places them again. */
  estimated: ReadonlySet<string>;
}

/** A card's height at the layout's width. */
export type WallHeightOf = (id: string) => number;
/** Whether a card's height is measured rather than estimated. */
export type WallMeasuredOf = (id: string) => boolean;

const ALWAYS_MEASURED: WallMeasuredOf = () => true;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function gutterFor(columns: number): number {
  return GUTTERS[columns] ?? WIDE_GUTTER;
}

function columnWidthAt(width: number, columns: number, gutter: number): number {
  return (width - gutter * (columns - 1)) / columns;
}

/** Four columns, fewer while a column would be under 220px (3, 2, 1). */
export function wallGeometry(containerWidth: number): WallGeometry {
  const pad = clamp(containerWidth * PAD_SHARE, MIN_PAD, MAX_PAD);
  const width = Math.max(
    0,
    Math.min(containerWidth - 2 * pad, COLUMN_MAX_WIDTH),
  );
  let columns = WALL_COLUMNS;
  while (
    columns > 1 &&
    columnWidthAt(width, columns, gutterFor(columns)) < MIN_COLUMN_WIDTH
  ) {
    columns -= 1;
  }
  const gutter =
    containerWidth < NARROW_FROM ? NARROW_GUTTER : gutterFor(columns);
  const columnWidth = Math.max(0, columnWidthAt(width, columns, gutter));
  return {
    columns,
    columnWidth,
    gutter,
    left: (containerWidth - width) / 2,
    width,
    widthBucket: Math.floor(columnWidth / WIDTH_BUCKET_PX),
    fontSize:
      Math.round(
        clamp(FONT_BASE + columnWidth * FONT_PER_PX, FONT_MIN, FONT_MAX) * 10,
      ) / 10,
    pinnedHeight: Math.round(
      clamp(columnWidth * PINNED_ASPECT, PINNED_MIN, PINNED_MAX),
    ),
  };
}

/**
 * The geometry as CSS variables on the scroller: the column, the gutter, the
 * cards' type size and the Pinned row height, for everything the masonry
 * does not place itself.
 */
export function wallCssVars(geometry: WallGeometry): CSSProperties {
  return {
    "--wall-cols": geometry.columns,
    "--wall-gap": `${geometry.gutter}px`,
    "--wall-width": `${geometry.width}px`,
    "--wall-fs": `${geometry.fontSize}px`,
    "--wall-pinned-h": `${geometry.pinnedHeight}px`,
  } as CSSProperties;
}

function emptyColumns(count: number): string[][] {
  return Array.from({ length: count }, () => []);
}

function shortestColumn(extents: readonly number[]): number {
  let shortest = 0;
  for (let index = 1; index < extents.length; index += 1) {
    if (extents[index]! < extents[shortest]!) shortest = index;
  }
  return shortest;
}

function columnExtents(
  columns: readonly string[][],
  heightOf: WallHeightOf,
  gutter: number,
): number[] {
  return columns.map((ids) =>
    ids.reduce((sum, id) => sum + heightOf(id) + gutter, 0),
  );
}

function place(
  geometry: WallGeometry,
  assignment: readonly string[][],
  heightOf: WallHeightOf,
  isMeasured: WallMeasuredOf,
): WallLayout {
  const { columnWidth, gutter, left } = geometry;
  const boxes = new Map<string, WallCardBox>();
  const estimated = new Set<string>();
  let height = 0;
  const columns = assignment.map((ids, column): WallColumn => {
    const x = left + column * (columnWidth + gutter);
    const tops: number[] = [];
    const heights: number[] = [];
    let top = 0;
    for (const id of ids) {
      const cardHeight = heightOf(id);
      if (!isMeasured(id)) estimated.add(id);
      tops.push(top);
      heights.push(cardHeight);
      boxes.set(id, {
        x,
        y: top,
        width: columnWidth,
        height: cardHeight,
        column,
      });
      top += cardHeight + gutter;
    }
    if (ids.length > 0) height = Math.max(height, top - gutter);
    return { ids, tops, heights };
  });
  return { geometry, columns, boxes, height, estimated };
}

/** Adds ids at the bottom of the shortest columns, in order. */
function fillColumns(
  columns: string[][],
  ids: readonly string[],
  heightOf: WallHeightOf,
  gutter: number,
): void {
  const extents = columnExtents(columns, heightOf, gutter);
  for (const id of ids) {
    const column = shortestColumn(extents);
    columns[column]!.push(id);
    extents[column]! += heightOf(id) + gutter;
  }
}

/** An arrival: the top of the shortest column. */
function insertTop(
  columns: string[][],
  id: string,
  heightOf: WallHeightOf,
  gutter: number,
): void {
  const extents = columnExtents(columns, heightOf, gutter);
  columns[shortestColumn(extents)]!.unshift(id);
}

/**
 * Cards first measured since they were placed on an estimate: from the
 * first of them in feed order, the cards refill the shortest columns; the
 * cards before it keep theirs.
 */
function replaceMeasured(
  columns: string[][],
  ids: readonly string[],
  previous: WallLayout,
  gutter: number,
  heightOf: WallHeightOf,
  isMeasured: WallMeasuredOf,
): string[][] {
  if (previous.estimated.size === 0) return columns;
  const from = ids.findIndex(
    (id) => previous.estimated.has(id) && isMeasured(id),
  );
  if (from < 0) return columns;
  const kept = new Set(ids.slice(0, from));
  const refilled = columns.map((column) => column.filter((id) => kept.has(id)));
  fillColumns(refilled, ids.slice(from), heightOf, gutter);
  return refilled;
}

interface WallChanges {
  removed: Set<string>;
  /** Arrivals before the first known card, newest first. */
  head: readonly string[];
  /** Cards after the last known one: a loaded page. */
  page: readonly string[];
}

/**
 * The feed as removals, arrivals and an appended page on top of the
 * previous layout, or null when it is not (no card is known, or a new card
 * landed between known ones): then the wall re-flows.
 */
function wallChanges(
  previous: WallLayout,
  ids: readonly string[],
): WallChanges | null {
  let first = -1;
  let last = -1;
  ids.forEach((id, index) => {
    if (!previous.boxes.has(id)) return;
    if (first === -1) first = index;
    last = index;
  });
  if (first === -1) return null;
  for (let index = first; index <= last; index += 1) {
    if (!previous.boxes.has(ids[index]!)) return null;
  }
  const present = new Set(ids);
  const removed = new Set<string>();
  for (const id of previous.boxes.keys()) if (!present.has(id)) removed.add(id);
  return { removed, head: ids.slice(0, first), page: ids.slice(last + 1) };
}

/**
 * The layout for `ids` at `geometry`, built on `previous` in one placement
 * pass: new heights (relayout within the kept columns), removals, arrivals
 * and an appended page keep every other card's column; a card measured for
 * the first time (`isMeasured`) places the cards again from it on. No
 * previous cards, a new column count or width bucket, or a feed reset runs
 * the full pass.
 */
export function reconcileWallLayout(
  previous: WallLayout | null,
  ids: readonly string[],
  geometry: WallGeometry,
  heightOf: WallHeightOf,
  isMeasured: WallMeasuredOf = ALWAYS_MEASURED,
): WallLayout {
  const { gutter } = geometry;
  const changes =
    previous !== null &&
    previous.boxes.size > 0 &&
    previous.geometry.columns === geometry.columns &&
    previous.geometry.widthBucket === geometry.widthBucket
      ? wallChanges(previous, ids)
      : null;
  if (!previous || !changes) {
    const columns = emptyColumns(geometry.columns);
    fillColumns(columns, ids, heightOf, gutter);
    return place(geometry, columns, heightOf, isMeasured);
  }

  const columns = previous.columns.map((column) =>
    column.ids.filter((id) => !changes.removed.has(id)),
  );
  for (let index = changes.head.length - 1; index >= 0; index -= 1) {
    insertTop(columns, changes.head[index]!, heightOf, gutter);
  }
  fillColumns(columns, changes.page, heightOf, gutter);
  return place(
    geometry,
    replaceMeasured(columns, ids, previous, gutter, heightOf, isMeasured),
    heightOf,
    isMeasured,
  );
}

/** A card that is not a frame (a new task or thing) and when it was made. */
export interface WallTimedItem {
  key: string;
  /** Epoch milliseconds. */
  createdAt: number;
}

/**
 * The feed with `items` placed among its frames by creation time, newest
 * first: each item goes before the first frame made before it. An item
 * older than the last loaded frame waits while more pages may come
 * (`hasMore`); without them it goes at the end. A frame without a known
 * time keeps its place.
 */
export function mergeNewItems(
  frameIds: readonly string[],
  items: readonly WallTimedItem[],
  createdAtOf: (frameId: string) => number | undefined,
  hasMore: boolean,
): readonly string[] {
  if (items.length === 0) return frameIds;
  const queue = newestFirst(items);
  const merged: string[] = [];
  let next = 0;
  for (const frameId of frameIds) {
    const frameAt = createdAtOf(frameId);
    if (frameAt !== undefined) {
      while (next < queue.length && queue[next]!.createdAt >= frameAt) {
        merged.push(queue[next]!.key);
        next += 1;
      }
    }
    merged.push(frameId);
  }
  if (!hasMore) {
    for (; next < queue.length; next += 1) merged.push(queue[next]!.key);
  }
  return merged;
}

/** A search's ranked frames, then the matching items, newest first. */
export function appendNewItems(
  frameIds: readonly string[],
  items: readonly WallTimedItem[],
): readonly string[] {
  if (items.length === 0) return frameIds;
  return [...frameIds, ...newestFirst(items).map((item) => item.key)];
}

function newestFirst(items: readonly WallTimedItem[]): WallTimedItem[] {
  return [...items].sort(
    (left, right) =>
      right.createdAt - left.createdAt || left.key.localeCompare(right.key),
  );
}
